// ── State-Space Modeling — Control Systems ────────────────────────────────────

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Colour palette ────────────────────────────────────────────────
const RED    = '#c0392b';
const DKRED  = '#7B0D0D';
const BLUE   = '#2E75B6';
const ORANGE = '#e07b00';
const GREEN  = '#27ae60';
const PURPLE = '#8e44ad';
const LTRED  = '#fff5f5';
const LTBLU  = '#eaf3ff';
const LTORG  = '#fff7e6';
const LTGRN  = '#f0fef4';
const LTPUR  = '#f5eeff';
const BORDER = '#f0cece';

// ── Reusable SVG defs ────────────────────────────────────────────
function Defs() {
  return (
    <defs>
      <marker id="aRed"    markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill={RED}/></marker>
      <marker id="aBlue"   markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill={BLUE}/></marker>
      <marker id="aOrange" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill={ORANGE}/></marker>
      <marker id="aGreen"  markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill={GREEN}/></marker>
      <marker id="aPurple" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill={PURPLE}/></marker>
      <marker id="aGray"   markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#888"/></marker>
    </defs>
  );
}

// ── Rounded-rect block helper ─────────────────────────────────────
function Block({ x, y, w, h, fill, stroke, label, sub, fontSize=12, subSize=10 }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={5} fill={fill} stroke={stroke} strokeWidth="1.5"/>
      <text x={x+w/2} y={y+h/2+(sub?-4:5)} textAnchor="middle" fontSize={fontSize} fill={stroke} fontWeight="700">{label}</text>
      {sub && <text x={x+w/2} y={y+h/2+10} textAnchor="middle" fontSize={subSize} fill={stroke} opacity="0.75">{sub}</text>}
    </g>
  );
}

// ── Arrow helper ─────────────────────────────────────────────────
function Arrow({ x1, y1, x2, y2, color='#555', marker='aGray', dash=false, label='', lx=0, ly=0 }) {
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.8"
        strokeDasharray={dash ? '5 3' : 'none'} markerEnd={`url(#${marker})`}/>
      {label && <text x={lx||((x1+x2)/2)} y={ly||((y1+y2)/2-5)} textAnchor="middle" fontSize="10" fill={color} fontWeight="600">{label}</text>}
    </g>
  );
}

// ── Info panel builder ────────────────────────────────────────────
function InfoPanel({ x, y, w, h, title, titleColor, items }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={5} fill="#fff" stroke={BORDER}/>
      <text x={x+w/2} y={y+18} textAnchor="middle" fontSize="13" fill={DKRED} fontWeight="700">{title}</text>
      {items.map(({ dy, bg, bc, lines }, i) => (
        <g key={i}>
          <rect x={x+6} y={y+dy} width={w-12} height={lines.length*16+10} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
          {lines.map((t, j) => (
            <text key={j} x={x+12} y={y+dy+16+j*15} fontSize={j===0?'11':'10'} fill={j===0?bc:'#444'} fontWeight={j===0?'700':'400'}>{t}</text>
          ))}
        </g>
      ))}
    </g>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 1 — State-Space Block Diagram
// ════════════════════════════════════════════════════════════════════
function DiagramBlockDiagram() {
  const W = 900, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>State-Space Block Diagram — Signal Flow of ẋ = Ax + Bu,  y = Cx + Du</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* ── summer ── */}
        <circle cx={130} cy={150} r={18} fill="#fff" stroke={RED} strokeWidth="1.8"/>
        <text x={130} y={155} textAnchor="middle" fontSize="16" fill={RED} fontWeight="700">Σ</text>

        {/* ── B matrix ── */}
        <Block x={176} y={126} w={64} h={48} fill={LTBLU} stroke={BLUE} label="B" sub="n×p matrix"/>

        {/* ── second summer (state update) ── */}
        <circle cx={306} cy={150} r={18} fill="#fff" stroke={RED} strokeWidth="1.8"/>
        <text x={306} y={155} textAnchor="middle" fontSize="16" fill={RED} fontWeight="700">Σ</text>

        {/* ── integrator ── */}
        <Block x={342} y={126} w={72} h={48} fill={LTGRN} stroke={GREEN} label="∫ dt" sub="integrator"/>

        {/* ── A matrix (feedback) ── */}
        <Block x={342} y={222} w={72} h={42} fill={LTRED} stroke={RED} label="A" sub="n×n matrix"/>

        {/* ── C matrix ── */}
        <Block x={476} y={126} w={64} h={48} fill={LTBLU} stroke={BLUE} label="C" sub="q×n matrix"/>

        {/* ── D matrix (feedthrough) ── */}
        <Block x={476} y={38} w={64} h={42} fill={LTORG} stroke={ORANGE} label="D" sub="q×p matrix"/>

        {/* ── output summer ── */}
        <circle cx={608} cy={150} r={18} fill="#fff" stroke={RED} strokeWidth="1.8"/>
        <text x={608} y={155} textAnchor="middle" fontSize="16" fill={RED} fontWeight="700">Σ</text>

        {/* ── signal labels ── */}
        {/* u(t) input */}
        <Arrow x1={20} y1={150} x2={112} y2={150} color={RED} marker="aRed"/>
        <text x={14} y={144} fontSize="13" fill={RED} fontWeight="700">u</text>
        <text x={14} y={157} fontSize="9" fill="#888">(p×1)</text>

        {/* u → B */}
        <Arrow x1={148} y1={150} x2={176} y2={150} color={BLUE} marker="aBlue"/>

        {/* Bu → summer2 */}
        <Arrow x1={240} y1={150} x2={288} y2={150} color={BLUE} marker="aBlue"/>
        <text x={258} y={143} fontSize="10" fill={BLUE} fontWeight="600">Bu</text>

        {/* ẋ → integrator */}
        <Arrow x1={324} y1={150} x2={342} y2={150} color={GREEN} marker="aGreen"/>
        <text x={330} y={143} fontSize="10" fill={GREEN} fontWeight="600">ẋ</text>

        {/* x out of integrator (state node) */}
        <Arrow x1={414} y1={150} x2={476} y2={150} color={RED} marker="aRed"/>
        <text x={443} y={143} fontSize="12" fill={RED} fontWeight="700">x</text>
        <text x={443} y={155} fontSize="9" fill="#888">(n×1)</text>

        {/* state node dot */}
        <circle cx={450} cy={150} r={5} fill={RED}/>

        {/* x → A feedback (down, left, up to summer2) */}
        <line x1={450} y1={150} x2={450} y2={243} stroke={RED} strokeWidth="1.8"/>
        <line x1={450} y1={243} x2={378} y2={243} stroke={RED} strokeWidth="1.8"/>
        <Arrow x1={378} y1={243} x2={306} y2={243} color={RED} marker="aRed"/>
        <Arrow x1={306} y1={234} x2={306} y2={168} color={RED} marker="aRed"/>
        <text x={390} y={258} fontSize="10" fill={RED} fontWeight="600">Ax</text>

        {/* Cx → output summer */}
        <Arrow x1={540} y1={150} x2={590} y2={150} color={BLUE} marker="aBlue"/>
        <text x={558} y={143} fontSize="10" fill={BLUE} fontWeight="600">Cx</text>

        {/* u → D (up branch) */}
        <line x1={80} y1={150} x2={80} y2={59} stroke={ORANGE} strokeWidth="1.8"/>
        <circle cx={80} cy={150} r={4} fill={ORANGE}/>
        <Arrow x1={80} y1={59} x2={476} y2={59} color={ORANGE} marker="aOrange"/>

        {/* Du → output summer */}
        <Arrow x1={540} y1={59} x2={608} y2={59} color={ORANGE} marker="aOrange"/>
        <line x1={608} y1={59} x2={608} y2={132} stroke={ORANGE} strokeWidth="1.8"/>
        <text x={568} y={52} fontSize="10" fill={ORANGE} fontWeight="600">Du</text>

        {/* y output */}
        <Arrow x1={626} y1={150} x2={700} y2={150} color={RED} marker="aRed"/>
        <text x={710} y={144} fontSize="13" fill={RED} fontWeight="700">y</text>
        <text x={710} y={157} fontSize="9" fill="#888">(q×1)</text>

        {/* Legend */}
        <rect x={730} y={20} width={158} height={260} rx={5} fill="#fff" stroke={BORDER}/>
        <text x={809} y={38} textAnchor="middle" fontSize="12" fill={DKRED} fontWeight="700">Matrices</text>
        {[
          { y:46, bg:LTRED,  bc:RED,    label:'A  (n×n)', desc:'System / state matrix' },
          { y:88, bg:LTBLU,  bc:BLUE,   label:'B  (n×p)', desc:'Input / control matrix' },
          { y:130, bg:LTBLU, bc:BLUE,   label:'C  (q×n)', desc:'Output matrix' },
          { y:172, bg:LTORG, bc:ORANGE, label:'D  (q×p)', desc:'Feedthrough matrix' },
          { y:214, bg:LTGRN, bc:GREEN,  label:'x  (n×1)', desc:'State vector' },
        ].map(({ y, bg, bc, label, desc }) => (
          <g key={y}>
            <rect x={736} y={y} width={146} height={38} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={742} y={y+15} fontSize="12" fill={bc} fontWeight="700">{label}</text>
            <text x={742} y={y+29} fontSize="10" fill="#444">{desc}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The state-space block diagram shows how input u(t) is transformed through the B matrix, added to the state feedback Ax, integrated to produce the state x(t), and then mapped to output y(t) through the C matrix. The D matrix provides direct feedthrough from input to output. This structure generalises to any number of inputs, outputs, and state variables.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 2 — Matrix Anatomy: what every entry means
// ════════════════════════════════════════════════════════════════════
function DiagramMatrixAnatomy() {
  const W = 900, H = 380;

  const cellW = 56, cellH = 44;
  const matrixLabel = (mx, my, rows, cols, title, subtitle, color, bg) => {
    const elements = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cx = mx + c * cellW + cellW / 2;
        const cy = my + r * cellH + cellH / 2;
        const isMain = r === c;
        elements.push(
          <g key={`${r}-${c}`}>
            <rect x={mx + c*cellW} y={my + r*cellH} width={cellW-2} height={cellH-2}
              rx={3} fill={isMain ? bg : '#fff'} stroke={isMain ? color : '#ddd'} strokeWidth={isMain?1.5:0.7}/>
            <text x={cx} y={cy+5} textAnchor="middle" fontSize="12" fill={isMain ? color : '#aaa'} fontWeight={isMain?'700':'400'} fontFamily="'Courier New',monospace">
              {`a${r+1}${c+1}`}
            </text>
          </g>
        );
      }
    }
    return (
      <g>
        {/* Bracket lines */}
        <line x1={mx-8} y1={my-4} x2={mx-8} y2={my+rows*cellH+4} stroke={color} strokeWidth="2.5"/>
        <line x1={mx+cols*cellW+6} y1={my-4} x2={mx+cols*cellW+6} y2={my+rows*cellH+4} stroke={color} strokeWidth="2.5"/>
        <line x1={mx-8} y1={my-4} x2={mx-2} y2={my-4} stroke={color} strokeWidth="2.5"/>
        <line x1={mx-8} y1={my+rows*cellH+4} x2={mx-2} y2={my+rows*cellH+4} stroke={color} strokeWidth="2.5"/>
        <line x1={mx+cols*cellW+6} y1={my-4} x2={mx+cols*cellW} y2={my-4} stroke={color} strokeWidth="2.5"/>
        <line x1={mx+cols*cellW+6} y1={my+rows*cellH+4} x2={mx+cols*cellW} y2={my+rows*cellH+4} stroke={color} strokeWidth="2.5"/>
        {elements}
        <text x={mx + cols*cellW/2} y={my - 16} textAnchor="middle" fontSize="14" fill={color} fontWeight="700">{title}</text>
        <text x={mx + cols*cellW/2} y={my - 4} textAnchor="middle" fontSize="9" fill="#888">{subtitle}</text>
      </g>
    );
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>State-Space Matrices — Anatomy and Physical Meaning</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* A matrix — 3×3 */}
        {matrixLabel(40, 80, 3, 3, 'A', 'System (n×n)', RED, LTRED)}
        {/* Diagonal annotation */}
        <line x1={155} y1={200} x2={195} y2={230} stroke={RED} strokeWidth="1" strokeDasharray="3 2"/>
        <rect x={196} y={220} width={210} height={36} rx={3} fill={LTRED} stroke={RED} strokeWidth="1"/>
        <text x={202} y={234} fontSize="10" fill={RED} fontWeight="700">Diagonal aᵢᵢ: self-coupling of state xᵢ</text>
        <text x={202} y={248} fontSize="10" fill="#444">Off-diagonal aᵢⱼ: coupling from xⱼ to ẋᵢ</text>

        {/* B matrix — 3×2 */}
        {matrixLabel(300, 80, 3, 2, 'B', 'Input (n×p)', BLUE, LTBLU)}
        <line x1={415} y1={145} x2={450} y2={145} stroke={BLUE} strokeWidth="1" strokeDasharray="3 2"/>
        <rect x={451} y={130} width={200} height={34} rx={3} fill={LTBLU} stroke={BLUE} strokeWidth="1"/>
        <text x={457} y={144} fontSize="10" fill={BLUE} fontWeight="700">Columns: how each input uⱼ</text>
        <text x={457} y={157} fontSize="10" fill="#444">drives the state derivatives ẋ</text>

        {/* C matrix — 2×3 */}
        {matrixLabel(40, 260, 2, 3, 'C', 'Output (q×n)', BLUE, LTBLU)}
        <line x1={210} y1={310} x2={250} y2={310} stroke={BLUE} strokeWidth="1" strokeDasharray="3 2"/>
        <rect x={251} y={296} width={200} height={34} rx={3} fill={LTBLU} stroke={BLUE} strokeWidth="1"/>
        <text x={257} y={310} fontSize="10" fill={BLUE} fontWeight="700">Rows: each output yᵢ = linear</text>
        <text x={257} y={323} fontSize="10" fill="#444">combination of states cᵢᵀx</text>

        {/* D matrix — 2×2 */}
        {matrixLabel(560, 260, 2, 2, 'D', 'Feedthrough (q×p)', ORANGE, LTORG)}
        <line x1={672} y1={310} x2={705} y2={310} stroke={ORANGE} strokeWidth="1" strokeDasharray="3 2"/>
        <rect x={706} y={296} width={180} height={34} rx={3} fill={LTORG} stroke={ORANGE} strokeWidth="1"/>
        <text x={712} y={310} fontSize="10" fill={ORANGE} fontWeight="700">Direct input → output path</text>
        <text x={712} y={323} fontSize="10" fill="#444">Often D=0 in physical systems</text>

        {/* State equation banner */}
        <rect x={460} y={65} width={425} height={160} rx={5} fill="#fff" stroke={BORDER}/>
        <text x={672} y={85} textAnchor="middle" fontSize="13" fill={DKRED} fontWeight="700">State-Space Equations</text>

        {[
          { y:100, color:RED,    mono:true,  t:'ẋ(t) = A·x(t) + B·u(t)' },
          { y:116, color:'#666', mono:false, t:'State equation — n first-order ODEs' },
          { y:140, color:BLUE,   mono:true,  t:'y(t) = C·x(t) + D·u(t)' },
          { y:156, color:'#666', mono:false, t:'Output equation — q algebraic equations' },
          { y:178, color:GREEN,  mono:true,  t:'ẋ = (A−BK)x  (closed-loop, D=0)' },
          { y:194, color:'#666', mono:false, t:'With state-feedback gain matrix K' },
          { y:212, color:PURPLE, mono:true,  t:'sX(s) = AX(s) + BU(s)  (Laplace)' },
        ].map(({ y, color, mono, t }) => (
          <text key={y} x={472} y={y} fontSize="11"
            fontFamily={mono ? "'Courier New',monospace" : "'Georgia',serif"}
            fill={color} fontWeight={mono ? '600' : '400'}>{t}</text>
        ))}
      </svg>
      <div style={S.dCaption}>
        The A matrix fully characterises the unforced dynamics — its diagonal entries represent self-coupling (decay or growth of each state) and off-diagonal entries represent inter-state coupling. B maps inputs to state derivatives, C maps states to outputs, and D provides instantaneous feedthrough. In most physical systems (motors, circuits, mechanical systems) D = 0.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 3 — State-Space ↔ Transfer Function Conversion
// ════════════════════════════════════════════════════════════════════
function DiagramSSvsTF() {
  const W = 900, H = 360;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>State-Space ↔ Transfer Function — Two Views of the Same System</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* Left panel: State-Space */}
        <rect x={10} y={10} width={390} height={340} rx={6} fill="#fff" stroke={BORDER}/>
        <rect x={10} y={10} width={390} height={36} rx={6} fill={RED} opacity="0.12"/>
        <text x={205} y={34} textAnchor="middle" fontSize="14" fill={RED} fontWeight="700">State-Space Representation</text>

        {[
          { y:62,  color:RED,    t:'ẋ = Ax + Bu',               desc:'n coupled 1st-order ODEs' },
          { y:90,  color:BLUE,   t:'y = Cx + Du',               desc:'q output equations' },
          { y:122, color:'#555', t:'Poles = eigenvalues of A',   desc:'λ: det(λI − A) = 0' },
          { y:150, color:'#555', t:'Stability: all Re(λᵢ) < 0', desc:'All eigenvalues in LHP' },
          { y:178, color:GREEN,  t:'n = order = # states',       desc:'# integrators in system' },
          { y:208, color:'#555', t:'Works for MIMO systems',     desc:'Multi-input / multi-output' },
          { y:236, color:'#555', t:'Internal states visible',   desc:'Controllability & observability' },
          { y:264, color:PURPLE, t:'Solution: x(t) = e^{At}x₀ + ∫₀ᵗ e^{A(t-τ)}Bu(τ)dτ', desc:'Matrix exponential' },
        ].map(({ y, color, t, desc }) => (
          <g key={y}>
            <text x={24} y={y} fontSize="12" fill={color} fontWeight="700" fontFamily="'Courier New',monospace">{t}</text>
            <text x={24} y={y+14} fontSize="10" fill="#666">{desc}</text>
          </g>
        ))}

        {/* Right panel: Transfer Function */}
        <rect x={500} y={10} width={390} height={340} rx={6} fill="#fff" stroke={BORDER}/>
        <rect x={500} y={10} width={390} height={36} rx={6} fill={BLUE} opacity="0.12"/>
        <text x={695} y={34} textAnchor="middle" fontSize="14" fill={BLUE} fontWeight="700">Transfer Function Representation</text>

        {[
          { y:62,  color:BLUE,   t:'G(s) = C(sI−A)⁻¹B + D',     desc:'Derived from SS by Laplace transform' },
          { y:90,  color:BLUE,   t:'G(s) = N(s)/D(s)',           desc:'Ratio of polynomials in s' },
          { y:122, color:'#555', t:'Poles = roots of det(sI−A)', desc:'Same as eigenvalues of A' },
          { y:150, color:'#555', t:'Zeros = roots of numerator', desc:'May cancel with poles' },
          { y:178, color:'#555', t:'SISO only (one TF per I/O)', desc:'Matrix of TFs for MIMO' },
          { y:208, color:'#555', t:'Hides unobservable states',  desc:'Pole-zero cancellation' },
          { y:236, color:'#555', t:'Easy Bode / Nyquist / RL',  desc:'Frequency domain design' },
          { y:264, color:PURPLE, t:'Y(s) = G(s)·U(s)',          desc:'Multiplication in s-domain' },
        ].map(({ y, color, t, desc }) => (
          <g key={y}>
            <text x={514} y={y} fontSize="12" fill={color} fontWeight="700" fontFamily="'Courier New',monospace">{t}</text>
            <text x={514} y={y+14} fontSize="10" fill="#666">{desc}</text>
          </g>
        ))}

        {/* Centre arrows */}
        <Arrow x1={402} y1={155} x2={498} y2={155} color={GREEN} marker="aGreen" label="SS → TF" ly={148} lx={450}/>
        <Arrow x1={498} y1={175} x2={402} y2={175} color={ORANGE} marker="aOrange" label="TF → SS" ly={190} lx={450}/>

        {/* SS→TF formula */}
        <rect x={380} y={200} width={140} height={48} rx={3} fill={LTGRN} stroke={GREEN} strokeWidth="1"/>
        <text x={450} y={215} textAnchor="middle" fontSize="10" fill={GREEN} fontWeight="700">G(s) = C(sI−A)⁻¹B+D</text>
        <text x={450} y={229} textAnchor="middle" fontSize="9" fill="#444">Laplace + algebra</text>
        <text x={450} y={241} textAnchor="middle" fontSize="9" fill="#444">unique result</text>

        {/* TF→SS note */}
        <rect x={380} y={258} width={140} height={48} rx={3} fill={LTORG} stroke={ORANGE} strokeWidth="1"/>
        <text x={450} y={273} textAnchor="middle" fontSize="10" fill={ORANGE} fontWeight="700">Many SS realizations</text>
        <text x={450} y={287} textAnchor="middle" fontSize="9" fill="#444">Controllable canonical</text>
        <text x={450} y={299} textAnchor="middle" fontSize="9" fill="#444">Observable canonical…</text>
      </svg>
      <div style={S.dCaption}>
        State-space and transfer function are two equivalent representations of the same LTI system. The transfer function G(s) = C(sI−A)⁻¹B + D is uniquely determined from the state-space matrices. Going the other way (TF → SS) yields infinitely many valid realizations (controllable canonical, observable canonical, diagonal/modal, etc.), all describing the same input-output behaviour but with different internal state definitions.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 4 — Eigenvalues, Modes & Stability
// ════════════════════════════════════════════════════════════════════
function DiagramEigenvalues() {
  const W = 900, H = 400;
  const px = 75, py = 30, pw = 400, ph = 340;
  const xR = [-4, 3], yR = [-3.5, 3.5];
  const toX = r => px + (r - xR[0]) / (xR[1] - xR[0]) * pw;
  const toY = i => py + (1 - (i - yR[0]) / (yR[1] - yR[0])) * ph;
  const ox = toX(0), oy = toY(0);

  const modes = [
    { s:[-2.5, 0],   color:GREEN,  label:'λ₁=−2.5',  type:'Stable real',         desc:'Exponential decay e^{−2.5t}' },
    { s:[-1, 1.8],   color:BLUE,   label:'λ₂=−1+j1.8',type:'Stable oscillatory', desc:'Decaying sinusoid' },
    { s:[-1,-1.8],   color:BLUE,   label:'λ₃=−1−j1.8',type:'Conj. pair',         desc:'(same mode, mirror)' },
    { s:[0.8, 0],    color:RED,    label:'λ₄=+0.8',   type:'Unstable real',       desc:'Exponential growth e^{+0.8t}' },
    { s:[0.3, 2.2],  color:ORANGE, label:'λ₅=0.3+j2.2',type:'Unstable oscillatory',desc:'Growing sinusoid' },
    { s:[0.3,-2.2],  color:ORANGE, label:'λ₆=0.3−j2.2',type:'Conj. pair',         desc:'(same mode, mirror)' },
    { s:[0, 1.5],    color:PURPLE, label:'λ₇=j1.5',   type:'Marginally stable',   desc:'Pure sinusoid (no decay)' },
    { s:[0,-1.5],    color:PURPLE, label:null,         type:null,                  desc:null },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Eigenvalues of A — System Modes and Stability</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* Plot area */}
        <rect x={px} y={py} width={pw} height={ph} rx={4} fill="#fff" stroke={BORDER}/>
        {/* Unstable shading */}
        <rect x={ox} y={py} width={px+pw-ox} height={ph} fill="#ffebee" opacity="0.35"/>
        <text x={ox+60} y={py+22} textAnchor="middle" fontSize="12" fill={RED} fontWeight="700" opacity="0.6">UNSTABLE</text>
        <text x={ox-80} y={py+22} textAnchor="middle" fontSize="12" fill={GREEN} fontWeight="700" opacity="0.6">STABLE</text>

        {/* Grid */}
        {[-3,-2,-1,0,1,2].map(v => (
          <g key={`gx${v}`}>
            <line x1={toX(v)} y1={py} x2={toX(v)} y2={py+ph} stroke="#f5f0f0" strokeWidth="1"/>
            {v!==0 && <text x={toX(v)} y={py+ph+14} textAnchor="middle" fontSize="10" fill="#ccc">{v}</text>}
          </g>
        ))}
        {[-3,-2,-1,1,2,3].map(v => (
          <g key={`gy${v}`}>
            <line x1={px} y1={toY(v)} x2={px+pw} y2={toY(v)} stroke="#f5f0f0" strokeWidth="1"/>
            <text x={px-6} y={toY(v)+4} textAnchor="end" fontSize="10" fill="#ccc">{v>0?`+${v}j`:`${v}j`}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={px} y1={oy} x2={px+pw} y2={oy} stroke="#ccc" strokeWidth="1.5"/>
        <line x1={ox} y1={py} x2={ox} y2={py+ph} stroke={RED} strokeWidth="1.8" strokeDasharray="6 3" opacity="0.5"/>
        <text x={px+pw+6} y={oy+4} fontSize="11" fill="#888">σ</text>
        <text x={ox+4} y={py+12} fontSize="11" fill="#888">jω</text>

        {/* Constant damping arc ζ=0.5 */}
        {(() => {
          const ang = Math.acos(0.5);
          return (
            <>
              <line x1={ox} y1={oy} x2={toX(-3.5*Math.cos(ang))} y2={toY(3.5*Math.sin(ang))}
                stroke="#ddd" strokeWidth="1" strokeDasharray="4 3"/>
              <line x1={ox} y1={oy} x2={toX(-3.5*Math.cos(ang))} y2={toY(-3.5*Math.sin(ang))}
                stroke="#ddd" strokeWidth="1" strokeDasharray="4 3"/>
              <text x={toX(-2.6*Math.cos(ang))-18} y={toY(2.6*Math.sin(ang))-4} fontSize="9" fill="#ccc">ζ=0.5</text>
            </>
          );
        })()}

        {/* Eigenvalue markers */}
        {modes.map(({ s, color, label }, i) => (
          <g key={i}>
            <circle cx={toX(s[0])} cy={toY(s[1])} r={7} fill={color} opacity="0.9"/>
            {label && (
              <text x={toX(s[0])+(s[0]>0?10:-10)} y={toY(s[1])+(s[1]>0?-8:14)}
                textAnchor={s[0]>0?'start':'end'} fontSize="10" fill={color} fontWeight="700">{label}</text>
            )}
          </g>
        ))}

        {/* Right panel */}
        <rect x={490} y={py} width={395} height={ph} rx={5} fill="#fff" stroke={BORDER}/>
        <text x={687} y={py+18} textAnchor="middle" fontSize="13" fill={DKRED} fontWeight="700">Eigenvalue → Mode Behaviour</text>

        {[
          { y:py+28, bg:LTGRN, bc:GREEN,  lines:['Stable real λ = −a  (a>0)','Purely exponential: e^{−at}','No oscillation; decays monotonically'] },
          { y:py+88, bg:LTBLU, bc:BLUE,   lines:['Stable complex λ = −σ ± jω','Decaying sinusoid: e^{−σt}sin(ωt)','Common in mechanical/electrical systems'] },
          { y:py+148, bg:LTRED, bc:RED,   lines:['Unstable real λ = +a  (a>0)','Exponential growth: e^{+at}','Any initial condition → diverges'] },
          { y:py+208, bg:LTORG, bc:ORANGE,lines:['Unstable complex λ = σ ± jω  (σ>0)','Growing oscillation: e^{σt}sin(ωt)','Amplitude increases with time'] },
          { y:py+268, bg:LTPUR, bc:PURPLE,lines:['Marginal λ = ±jω  (σ=0)','Pure sinusoid: sin(ωt)','Neither decays nor grows; on boundary'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={496} y={y} width={383} height={56} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={502} y={y+16+i*16} fontSize={i===0?'12':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Each eigenvalue λᵢ of the A matrix corresponds to a natural mode of the system. The real part σ = Re(λ) determines growth or decay; the imaginary part ω = Im(λ) determines oscillation frequency. Stability requires all eigenvalues to have strictly negative real parts (left-half plane). The eigenvalues of A are identical to the poles of the transfer function G(s).
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 5 — Phase Portrait (2D state space trajectories)
// ════════════════════════════════════════════════════════════════════
function DiagramPhasePortrait() {
  const W = 900, H = 370;

  // Three panels: stable node, stable spiral, unstable spiral
  const panels = [
    {
      x:10, title:'Stable Node', subtitle:'eigenvalues: −1, −2',
      color:GREEN, bg:LTGRN,
      // trajectories converge to origin
      trajs:[
        [[3,1],[2.4,0.7],[1.8,0.4],[1.2,0.2],[0.6,0.05],[0.1,0]],
        [[-3,-1],[-2.4,-0.7],[-1.8,-0.4],[-1.2,-0.2],[-0.6,-0.05],[-0.1,0]],
        [[1,3],[0.7,2.1],[0.4,1.3],[0.2,0.7],[0.05,0.2],[0,0.02]],
        [[-1,-3],[-0.7,-2.1],[-0.4,-1.3],[-0.2,-0.7],[-0.05,-0.2],[0,-0.02]],
        [[3,-2],[2.4,-1.4],[1.8,-0.9],[1.2,-0.5],[0.6,-0.15],[0.1,0]],
        [[-3,2],[-2.4,1.4],[-1.8,0.9],[-1.2,0.5],[-0.6,0.15],[-0.1,0]],
      ],
    },
    {
      x:305, title:'Stable Spiral', subtitle:'eigenvalues: −0.5 ± j2',
      color:BLUE, bg:LTBLU,
      trajs: (() => {
        const t = [];
        for (let a0 = 0; a0 < 2*Math.PI; a0 += Math.PI/2) {
          const pts = [];
          for (let k = 0; k <= 40; k++) {
            const tt = k * 0.2;
            const r = 3 * Math.exp(-0.5*tt);
            pts.push([r*Math.cos(a0+2*tt), r*Math.sin(a0+2*tt)]);
          }
          t.push(pts);
        }
        return t;
      })(),
    },
    {
      x:600, title:'Unstable Spiral', subtitle:'eigenvalues: +0.3 ± j2',
      color:RED, bg:LTRED,
      trajs: (() => {
        const t = [];
        for (let a0 = 0; a0 < 2*Math.PI; a0 += Math.PI/2) {
          const pts = [];
          for (let k = 0; k <= 25; k++) {
            const tt = k * 0.18;
            const r = 0.3 * Math.exp(0.3*tt);
            pts.push([r*Math.cos(a0+2*tt), r*Math.sin(a0+2*tt)]);
          }
          t.push(pts);
        }
        return t;
      })(),
    },
  ];

  const PW = 270, PH = 290;
  const sc = 40;
  const toCX = (px, rx) => px + PW/2 + rx*sc;
  const toCY = (py, ry) => py + 50 + PH/2 - ry*sc;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Phase Portraits — Trajectories in 2D State Space (x₁ vs x₂)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>
        {panels.map(({ x, title, subtitle, color, bg, trajs }) => {
          const py2 = 20;
          const ox2 = toCX(x, 0), oy2 = toCY(py2, 0);
          return (
            <g key={x}>
              <rect x={x} y={py2} width={PW} height={PH+50} rx={5} fill="#fff" stroke={BORDER}/>
              <text x={x+PW/2} y={py2+18} textAnchor="middle" fontSize="13" fill={color} fontWeight="700">{title}</text>
              <text x={x+PW/2} y={py2+32} textAnchor="middle" fontSize="10" fill="#777" fontStyle="italic">{subtitle}</text>

              {/* Axes */}
              <line x1={x+14} y1={oy2} x2={x+PW-14} y2={oy2} stroke="#ddd" strokeWidth="1.3"/>
              <line x1={ox2} y1={py2+42} x2={ox2} y2={py2+PH+18} stroke="#ddd" strokeWidth="1.3"/>
              <text x={x+PW-16} y={oy2+12} fontSize="10" fill="#aaa">x₁</text>
              <text x={ox2+4} y={py2+48} fontSize="10" fill="#aaa">x₂</text>

              {/* Origin */}
              <circle cx={ox2} cy={oy2} r={5} fill={color} opacity="0.8"/>

              {/* Trajectories */}
              {trajs.map((traj, ti) => {
                const pts = traj
                  .filter(([rx, ry]) => {
                    const sx = toCX(x, rx), sy = toCY(py2, ry);
                    return sx > x+10 && sx < x+PW-10 && sy > py2+40 && sy < py2+PH+14;
                  })
                  .map(([rx, ry]) => `${toCX(x, rx)},${toCY(py2, ry)}`).join(' ');
                return pts.length > 4 ? (
                  <polyline key={ti} points={pts} fill="none" stroke={color} strokeWidth="1.8" opacity="0.7"/>
                ) : null;
              })}

              {/* Direction arrows on one trajectory */}
              {trajs[0] && trajs[0].length > 5 && (() => {
                const mid = Math.floor(trajs[0].length * 0.4);
                const [rx1, ry1] = trajs[0][mid];
                const [rx2, ry2] = trajs[0][mid+1] || trajs[0][mid];
                const ax = toCX(x, rx1), ay = toCY(py2, ry1);
                const bx = toCX(x, rx2), by2b = toCY(py2, ry2);
                if (ax > x+14 && ax < x+PW-14 && ay > py2+42 && ay < py2+PH+14) {
                  const ang = Math.atan2(by2b-ay, bx-ax) * 180/Math.PI;
                  return (
                    <polygon points={`${ax},${ay} ${ax-8},${ay-4} ${ax-8},${ay+4}`}
                      fill={color} opacity="0.8"
                      transform={`rotate(${ang},${ax},${ay})`}/>
                  );
                }
                return null;
              })()}

              {/* Info box */}
              <rect x={x+8} y={py2+PH+22} width={PW-16} height={34} rx={3} fill={bg} stroke={color} strokeWidth="1"/>
              <text x={x+14} y={py2+PH+36} fontSize="10" fill={color} fontWeight="700">{
                color===GREEN ? 'All trajectories → origin (equilibrium)' :
                color===BLUE  ? 'Trajectories spiral inward to origin' :
                               'Trajectories spiral outward → ∞'
              }</text>
              <text x={x+14} y={py2+PH+50} fontSize="9" fill="#444">{
                color===GREEN ? 'Purely real eigenvalues; no rotation' :
                color===BLUE  ? 'Complex eigenvalues with σ < 0' :
                               'Complex eigenvalues with σ > 0'
              }</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Phase portraits show trajectories of the 2D state vector (x₁, x₂) over time. A stable node has two real negative eigenvalues: trajectories collapse straight to the origin. A stable spiral has complex eigenvalues with negative real part: trajectories corkscrew inward. An unstable spiral has positive real part: every trajectory spirals outward. The equilibrium (origin) is the fixed point of ẋ = Ax.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 6 — Controllability & Observability
// ════════════════════════════════════════════════════════════════════
function DiagramControllabilityObservability() {
  const W = 900, H = 400;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Controllability & Observability — The Two Fundamental Structural Properties</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* ── LEFT: Controllability ── */}
        <rect x={10} y={10} width={420} height={380} rx={6} fill="#fff" stroke={BORDER}/>
        <rect x={10} y={10} width={420} height={36} rx={6} fill={BLUE} opacity="0.10"/>
        <text x={220} y={34} textAnchor="middle" fontSize="14" fill={BLUE} fontWeight="700">Controllability</text>

        {/* Visual: input reaching all states */}
        {/* Block diagram: u → system with arrows to states */}
        <Block x={30} y={65} w={50} h={36} fill={LTBLU} stroke={BLUE} label="u"/>
        {/* States */}
        {['x₁','x₂','x₃'].map((lbl, i) => (
          <Block key={lbl} x={200} y={58+i*56} w={50} h={36} fill={i===0?LTGRN:i===1?LTBLU:LTRED} stroke={i===0?GREEN:i===1?BLUE:RED} label={lbl}/>
        ))}
        {/* Arrows: controllable */}
        <Arrow x1={80} y1={83} x2={200} y2={76}  color={BLUE} marker="aBlue"/>
        <Arrow x1={80} y1={83} x2={200} y2={132} color={BLUE} marker="aBlue"/>
        <Arrow x1={80} y1={83} x2={200} y2={188} color={BLUE} marker="aBlue"/>
        <text x={140} y={75} fontSize="9" fill={BLUE}>B cols reach</text>
        <text x={140} y={87} fontSize="9" fill={BLUE}>all states</text>

        {/* Rank condition */}
        <rect x={20} y={240} width={400} height={140} rx={4} fill={LTBLU} stroke={BLUE} strokeWidth="1"/>
        <text x={220} y={258} textAnchor="middle" fontSize="12" fill={BLUE} fontWeight="700">Controllability Matrix 𝒞</text>
        <text x={30} y={278} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">𝒞 = [B  AB  A²B  ···  Aⁿ⁻¹B]</text>
        <text x={30} y={298} fontSize="11" fill={BLUE} fontWeight="700">rank(𝒞) = n  →  Controllable</text>
        <text x={30} y={318} fontSize="10" fill="#555">All states reachable from any initial condition</text>
        <text x={30} y={336} fontSize="10" fill="#555">in finite time with some input u(t)</text>
        <text x={30} y={356} fontSize="10" fill={RED} fontWeight="700">rank(𝒞) &lt; n  →  NOT Controllable</text>
        <text x={30} y={372} fontSize="10" fill="#555">Some states unreachable — those modes</text>

        {/* Uncontrollable visual */}
        <Block x={280} y={65} w={50} h={36} fill={LTBLU} stroke={BLUE} label="u"/>
        {['x₁','x₂','x₃'].map((lbl, i) => (
          <Block key={`unc${lbl}`} x={370} y={58+i*56} w={50} h={36}
            fill={i===2?'#f5f5f5':i===0?LTGRN:LTBLU}
            stroke={i===2?'#bbb':i===0?GREEN:BLUE} label={lbl}/>
        ))}
        <Arrow x1={330} y1={83} x2={370} y2={76}  color={BLUE} marker="aBlue"/>
        <Arrow x1={330} y1={83} x2={370} y2={132} color={BLUE} marker="aBlue"/>
        {/* x₃ unreachable */}
        <line x1={330} y1={83} x2={370} y2={188} stroke="#ddd" strokeWidth="1.5" strokeDasharray="4 2"/>
        <text x={350} y={192} textAnchor="middle" fontSize="9" fill="#bbb">✗ no path</text>
        <text x={290} y={220} fontSize="10" fill={RED} fontWeight="700">Un-controllable</text>

        {/* ── RIGHT: Observability ── */}
        <rect x={460} y={10} width={430} height={380} rx={6} fill="#fff" stroke={BORDER}/>
        <rect x={460} y={10} width={430} height={36} rx={6} fill={ORANGE} opacity="0.10"/>
        <text x={675} y={34} textAnchor="middle" fontSize="14" fill={ORANGE} fontWeight="700">Observability</text>

        {/* Visual */}
        {['x₁','x₂','x₃'].map((lbl, i) => (
          <Block key={`obs${lbl}`} x={470} y={58+i*56} w={50} h={36}
            fill={i===0?LTGRN:i===1?LTBLU:LTRED} stroke={i===0?GREEN:i===1?BLUE:RED} label={lbl}/>
        ))}
        <Block x={640} y={65} w={50} h={36} fill={LTORG} stroke={ORANGE} label="y"/>
        <Arrow x1={520} y1={76}  x2={640} y2={83} color={ORANGE} marker="aOrange"/>
        <Arrow x1={520} y1={132} x2={640} y2={83} color={ORANGE} marker="aOrange"/>
        <Arrow x1={520} y1={188} x2={640} y2={83} color={ORANGE} marker="aOrange"/>
        <text x={575} y={75} textAnchor="middle" fontSize="9" fill={ORANGE}>C rows</text>
        <text x={575} y={87} textAnchor="middle" fontSize="9" fill={ORANGE}>sense all states</text>

        {/* Rank condition */}
        <rect x={470} y={240} width={410} height={140} rx={4} fill={LTORG} stroke={ORANGE} strokeWidth="1"/>
        <text x={675} y={258} textAnchor="middle" fontSize="12" fill={ORANGE} fontWeight="700">Observability Matrix 𝒪</text>
        <text x={480} y={278} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">𝒪 = [C;  CA;  CA²;  ···;  CAⁿ⁻¹]ᵀ</text>
        <text x={480} y={298} fontSize="11" fill={ORANGE} fontWeight="700">rank(𝒪) = n  →  Observable</text>
        <text x={480} y={318} fontSize="10" fill="#555">All initial states x₀ uniquely identifiable</text>
        <text x={480} y={336} fontSize="10" fill="#555">from output y(t) over some finite interval</text>
        <text x={480} y={356} fontSize="10" fill={RED} fontWeight="700">rank(𝒪) &lt; n  →  NOT Observable</text>
        <text x={480} y={372} fontSize="10" fill="#555">Some state modes hidden from the output</text>

        {/* Unobservable visual */}
        {['x₁','x₂','x₃'].map((lbl, i) => (
          <Block key={`uno${lbl}`} x={700} y={58+i*56} w={50} h={36}
            fill={i===2?'#f5f5f5':i===0?LTGRN:LTBLU}
            stroke={i===2?'#bbb':i===0?GREEN:BLUE} label={lbl}/>
        ))}
        <Block x={820} y={65} w={50} h={36} fill={LTORG} stroke={ORANGE} label="y"/>
        <Arrow x1={750} y1={76}  x2={820} y2={83} color={ORANGE} marker="aOrange"/>
        <Arrow x1={750} y1={132} x2={820} y2={83} color={ORANGE} marker="aOrange"/>
        <line x1={750} y1={188} x2={820} y2={188} stroke="#ddd" strokeWidth="1.5" strokeDasharray="4 2"/>
        <text x={786} y={200} textAnchor="middle" fontSize="9" fill="#bbb">✗ not sensed</text>
        <text x={740} y={220} fontSize="10" fill={RED} fontWeight="700">Un-observable</text>
      </svg>
      <div style={S.dCaption}>
        Controllability asks: can we drive the system from any initial state to any desired state using the input u? Observability asks: can we determine the initial state x₀ from the output y alone? Both are tested by rank conditions on their respective matrices. For full state-feedback pole placement to work, the system must be controllable. For a state observer (Luenberger) to work, the system must be observable.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 7 — Full-State Feedback & Pole Placement
// ════════════════════════════════════════════════════════════════════
function DiagramPolePlacement() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Full-State Feedback — Pole Placement via u = −Kx</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* ── BLOCK DIAGRAM ── */}
        {/* Reference input r */}
        <Arrow x1={14} y1={120} x2={62} y2={120} color={RED} marker="aRed"/>
        <text x={8} y={114} fontSize="13" fill={RED} fontWeight="700">r</text>

        {/* Summer */}
        <circle cx={80} cy={120} r={18} fill="#fff" stroke={RED} strokeWidth="1.8"/>
        <text x={80} y={125} textAnchor="middle" fontSize="16" fill={RED} fontWeight="700">Σ</text>

        {/* Plant block */}
        <Block x={172} y={96} w={110} h={48} fill={LTBLU} stroke={BLUE} label="Plant" sub="ẋ=Ax+Bu, y=Cx"/>

        {/* Arrow into plant */}
        <Arrow x1={98} y1={120} x2={172} y2={120} color={RED} marker="aRed"/>
        <text x={130} y={113} fontSize="11" fill={RED} fontWeight="700">u</text>

        {/* State output node */}
        <circle cx={340} cy={120} r={5} fill={BLUE}/>
        <Arrow x1={282} y1={120} x2={430} y2={120} color={BLUE} marker="aBlue"/>
        <text x={356} y={113} fontSize="12" fill={BLUE} fontWeight="700">x (states)</text>

        {/* Output y */}
        <Block x={430} y={96} w={80} h={48} fill={LTGRN} stroke={GREEN} label="C" sub="output map"/>
        <Arrow x1={510} y1={120} x2={580} y2={120} color={GREEN} marker="aGreen"/>
        <text x={592} y={124} fontSize="13" fill={GREEN} fontWeight="700">y</text>

        {/* K gain block (feedback) */}
        <Block x={200} y={210} w={100} h={44} fill={LTRED} stroke={RED} label="−K" sub="gain matrix (1×n)"/>

        {/* State → K (down then to K) */}
        <line x1={340} y1={120} x2={340} y2={232} stroke={RED} strokeWidth="1.8"/>
        <Arrow x1={340} y1={232} x2={300} y2={232} color={RED} marker="aRed"/>

        {/* K → summer */}
        <line x1={200} y1={232} x2={80} y2={232} stroke={RED} strokeWidth="1.8"/>
        <Arrow x1={80} y1={232} x2={80} y2={138} color={RED} marker="aRed"/>
        <text x={66} y={196} fontSize="10" fill={RED} fontWeight="600">−Kx</text>

        {/* Closed-loop annotation */}
        <rect x={30} y={268} width={340} height={58} rx={4} fill={LTRED} stroke={RED} strokeWidth="1"/>
        <text x={38} y={284} fontSize="11" fill={RED} fontWeight="700">Closed-loop: ẋ = (A − BK)x + Br</text>
        <text x={38} y={300} fontSize="10" fill="#444">Eigenvalues of (A − BK) = desired poles</text>
        <text x={38} y={316} fontSize="10" fill="#444">Choose K so poles meet performance specs</text>

        {/* s-plane: before and after */}
        <rect x={390} y={60} width={500} height={268} rx={5} fill="#fff" stroke={BORDER}/>
        <text x={640} y={80} textAnchor="middle" fontSize="12" fill={DKRED} fontWeight="700">Pole Placement: Moving Eigenvalues</text>

        {/* mini s-plane */}
        {(() => {
          const mx = 400, my = 90, mw = 230, mh = 220;
          const xR = [-5,2], yR = [-3,3];
          const toX = r => mx + (r-xR[0])/(xR[1]-xR[0])*mw;
          const toY = i => my + (1-(i-yR[0])/(yR[1]-yR[0]))*mh;
          const ox = toX(0), oy2 = toY(0);
          return (
            <g>
              <rect x={mx} y={my} width={mw} height={mh} rx={3} fill="#fafafa" stroke={BORDER}/>
              <rect x={ox} y={my} width={mx+mw-ox} height={mh} fill="#ffebee" opacity="0.3"/>
              <line x1={mx} y1={oy2} x2={mx+mw} y2={oy2} stroke="#ccc" strokeWidth="1"/>
              <line x1={ox} y1={my} x2={ox} y2={my+mh} stroke={RED} strokeWidth="1.3" strokeDasharray="5 3" opacity="0.5"/>
              <text x={ox+4} y={my+12} fontSize="9" fill={RED} opacity="0.6">Im axis</text>

              {/* Open-loop poles (circles) */}
              {[[-0.5,2],[-0.5,-2],[0.8,0]].map(([r,i],idx) => (
                <g key={`ol${idx}`}>
                  <circle cx={toX(r)} cy={toY(i)} r={6} fill="none" stroke={ORANGE} strokeWidth="2"/>
                  <text x={toX(r)+8} y={toY(i)+3} fontSize="9" fill={ORANGE}>OL</text>
                </g>
              ))}

              {/* Desired poles (filled) */}
              {[[-2.5,1.5],[-2.5,-1.5],[-3,0]].map(([r,i],idx) => (
                <g key={`cl${idx}`}>
                  <circle cx={toX(r)} cy={toY(i)} r={6} fill={BLUE} opacity="0.85"/>
                  <text x={toX(r)-10} y={toY(i)-8} fontSize="9" fill={BLUE} fontWeight="700">CL</text>
                </g>
              ))}

              {/* Arrows showing movement */}
              {[
                [[-0.5,2],[-2.5,1.5]],
                [[-0.5,-2],[-2.5,-1.5]],
                [[0.8,0],[-3,0]],
              ].map(([[r1,i1],[r2,i2]],idx) => (
                <Arrow key={`mv${idx}`}
                  x1={toX(r1)} y1={toY(i1)} x2={toX(r2)} y2={toY(i2)}
                  color={GREEN} marker="aGreen" dash={false}/>
              ))}

              <text x={mx+mw/2} y={my+mh+16} textAnchor="middle" fontSize="9" fill="#888">s-plane</text>
              <text x={toX(-0.5)+8} y={my+mh-10} fontSize="8" fill={ORANGE}>○ open-loop poles</text>
              <text x={toX(-0.5)+8} y={my+mh-0} fontSize="8" fill={BLUE}>● desired closed-loop</text>
            </g>
          );
        })()}

        {/* Ackermann's formula box */}
        <rect x={645} y={90} width={238} height={230} rx={4} fill="#fff" stroke={BORDER}/>
        <text x={764} y={108} textAnchor="middle" fontSize="11" fill={DKRED} fontWeight="700">Design Methods</text>
        {[
          { y:116, bg:LTBLU, bc:BLUE,   lines:["Ackermann's Formula","K = eₙᵀ 𝒞⁻¹ φ(A)","One formula for SISO systems"] },
          { y:166, bg:LTGRN, bc:GREEN,  lines:["Bass-Gura Method","Place poles via char. poly matching","Compare desired vs actual coefficients"] },
          { y:216, bg:LTORG, bc:ORANGE, lines:["MATLAB: place / acker","K = place(A,B,desired_poles)","Numerical; handles MIMO"] },
          { y:266, bg:LTPUR, bc:PURPLE, lines:["LQR (optimal)","Minimise J=∫(xᵀQx+uᵀRu)dt","Q,R weights trade off speed vs effort"] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={651} y={y} width={226} height={46} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={657} y={y+14+i*14} fontSize={i===0?'11':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Full-state feedback u = −Kx + r replaces the open-loop A matrix with the closed-loop system matrix A−BK. By choosing the gain matrix K, the designer moves the eigenvalues of A−BK to any desired locations in the s-plane (provided the system is controllable). The closed-loop poles determine stability and transient performance — the same specifications used in root locus design.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 8 — Luenberger Observer + Observer-Based Control
// ════════════════════════════════════════════════════════════════════
function DiagramObserver() {
  const W = 900, H = 360;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Luenberger Observer — Estimating States from Output Measurements</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* ── TOP HALF: ACTUAL PLANT ── */}
        <text x={14} y={30} fontSize="11" fill="#888" fontStyle="italic">Actual Plant (physical system)</text>
        <rect x={10} y={34} width={560} height={2} fill="#f0cece"/>

        {/* Input u */}
        <Arrow x1={14} y1={80} x2={80} y2={80} color={RED} marker="aRed"/>
        <text x={8} y={75} fontSize="12" fill={RED} fontWeight="700">u</text>

        {/* Plant */}
        <Block x={80} y={56} w={120} h={48} fill={LTBLU} stroke={BLUE} label="PLANT" sub="ẋ=Ax+Bu"/>
        {/* State output (hidden) */}
        <Arrow x1={200} y1={80} x2={260} y2={80} color={BLUE} marker="aBlue"/>
        <text x={222} y={73} fontSize="11" fill={BLUE} fontWeight="700">x(t)</text>
        <text x={218} y={85} fontSize="8" fill="#888">(unknown)</text>

        {/* C measurement */}
        <Block x={260} y={56} w={80} h={48} fill={LTGRN} stroke={GREEN} label="C" sub="sensor"/>
        {/* Measured output y */}
        <Arrow x1={340} y1={80} x2={440} y2={80} color={GREEN} marker="aGreen"/>
        <text x={385} y={73} fontSize="12" fill={GREEN} fontWeight="700">y(t)</text>
        <text x={380} y={85} fontSize="9" fill="#888">(measured)</text>

        {/* ── BOTTOM HALF: OBSERVER ── */}
        <text x={14} y={168} fontSize="11" fill="#888" fontStyle="italic">Luenberger Observer (software model running in parallel)</text>
        <rect x={10} y={172} width={560} height={2} fill="#f0cece"/>

        {/* u also goes to observer */}
        <line x1={44} y1={80} x2={44} y2={220} stroke={RED} strokeWidth="1.8"/>
        <circle cx={44} cy={80} r={4} fill={RED}/>
        <Arrow x1={44} y1={220} x2={80} y2={220} color={RED} marker="aRed"/>

        {/* Observer block */}
        <Block x={80} y={196} w={140} h={48} fill={LTPUR} stroke={PURPLE} label="OBSERVER" sub="x̂̇=Ax̂+Bu+L(y−Cx̂)"/>

        {/* Estimated state output */}
        <Arrow x1={220} y1={220} x2={300} y2={220} color={PURPLE} marker="aPurple"/>
        <text x={224} y={213} fontSize="11" fill={PURPLE} fontWeight="700">x̂(t)</text>
        <text x={220} y={226} fontSize="8" fill="#888">(estimated)</text>

        {/* C applied to estimate */}
        <Block x={300} y={196} w={80} h={48} fill={LTGRN} stroke={GREEN} label="C" sub="×x̂"/>

        {/* Estimated output */}
        <Arrow x1={380} y1={220} x2={440} y2={220} color={GREEN} marker="aGreen"/>
        <text x={384} y={213} fontSize="11" fill={GREEN} fontWeight="700">ŷ(t)</text>
        <text x={380} y={226} fontSize="9" fill="#888">(estimated)</text>

        {/* Innovation summer */}
        <circle cx={460} cy={150} r={18} fill="#fff" stroke={ORANGE} strokeWidth="1.8"/>
        <text x={460} y={155} textAnchor="middle" fontSize="16" fill={ORANGE} fontWeight="700">Σ</text>

        {/* y → summer */}
        <line x1={440} y1={80} x2={460} y2={80} stroke={GREEN} strokeWidth="1.8"/>
        <Arrow x1={460} y1={80} x2={460} y2={132} color={GREEN} marker="aGreen"/>
        {/* ŷ → summer (negative) */}
        <line x1={440} y1={220} x2={460} y2={220} stroke={GREEN} strokeWidth="1.8"/>
        <Arrow x1={460} y1={220} x2={460} y2={168} color={GREEN} marker="aGreen"/>
        <text x={468} y={218} fontSize="9" fill={GREEN}>−ŷ</text>

        {/* Innovation e = y − ŷ */}
        <Arrow x1={478} y1={150} x2={560} y2={150} color={ORANGE} marker="aOrange"/>
        <text x={496} y={143} fontSize="11" fill={ORANGE} fontWeight="700">e = y−ŷ</text>
        <text x={490} y={157} fontSize="9" fill="#888">innovation</text>

        {/* L gain */}
        <Block x={560} y={126} w={70} h={48} fill={LTORG} stroke={ORANGE} label="L" sub="observer gain"/>

        {/* L(y−ŷ) correction back to observer */}
        <Arrow x1={630} y1={150} x2={680} y2={150} color={ORANGE} marker="aOrange"/>
        <line x1={680} y1={150} x2={680} y2={220} stroke={ORANGE} strokeWidth="1.8"/>
        <Arrow x1={680} y1={220} x2={220} y2={220} color={ORANGE} marker="aOrange"/>
        <text x={655} y={143} fontSize="9" fill={ORANGE} fontWeight="600">L·e</text>

        {/* x̂ used for feedback control */}
        <circle cx={300} cy={220} r={4} fill={PURPLE}/>
        <line x1={300} y1={220} x2={300} y2={300} stroke={PURPLE} strokeWidth="1.8"/>
        <Arrow x1={300} y1={300} x2={44} y2={300} color={PURPLE} marker="aPurple"/>
        <Arrow x1={44} y1={300} x2={44} y2={240} color={PURPLE} marker="aPurple"/>
        <Block x={80} y={280} w={100} h={40} fill={LTRED} stroke={RED} label="−K" sub="state feedback"/>
        <text x={200} y={298} fontSize="9" fill={RED} fontWeight="600">u = −Kx̂</text>
        <Arrow x1={180} y1={290} x2={80} y2={290} color={RED} marker="aRed"/>

        {/* Right info panel */}
        <rect x={710} y={20} width={178} height={330} rx={5} fill="#fff" stroke={BORDER}/>
        <text x={799} y={38} textAnchor="middle" fontSize="12" fill={DKRED} fontWeight="700">Observer Design</text>
        {[
          { y:46,  bg:LTPUR, bc:PURPLE, lines:["Observer equation","x̂̇ = Ax̂+Bu+L(y−Cx̂)","= (A−LC)x̂ + Bu + Ly"] },
          { y:108, bg:LTORG, bc:ORANGE, lines:["Error dynamics","ė = (A−LC)e","e→0 if eig(A−LC) in LHP"] },
          { y:170, bg:LTBLU, bc:BLUE,   lines:["L design","Place eig(A−LC) faster","than closed-loop poles"] },
          { y:232, bg:LTGRN, bc:GREEN,  lines:["Separation principle","Design K and L independently","Poles = eig(A−BK) ∪ eig(A−LC)"] },
          { y:294, bg:LTRED, bc:RED,    lines:["Duality","Observer design ≡ pole","placement for (Aᵀ, Cᵀ, Bᵀ)"] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={716} y={y} width={166} height={58} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={722} y={y+16+i*16} fontSize={i===0?'11':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        When full state measurement is unavailable (the common case), a Luenberger observer reconstructs x̂ by running a copy of the plant model in software. The observer gain L is injected to correct the model using the output error e = y − Cx̂. The observer-based controller uses u = −Kx̂ instead of −Kx. The separation principle guarantees that K and L can be designed independently, with the combined system poles being the union of the two sets of designed eigenvalues.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 9 — Canonical Forms side-by-side
// ════════════════════════════════════════════════════════════════════
function DiagramCanonicalForms() {
  const W = 900, H = 330;

  const renderMatrix = (x, y, rows, color, label) => {
    const cw = 52, ch = 36;
    const n = rows.length, m = rows[0].length;
    const els = rows.flatMap((row, r) =>
      row.map((val, c) => (
        <g key={`${r}-${c}`}>
          <rect x={x+c*cw} y={y+r*ch} width={cw-2} height={ch-2} rx={2}
            fill={val==='0'?'#f9f9f9':val==='1'||val==='-1'?color==='RED'?LTRED:LTBLU:'#fff5fb'}
            stroke={val==='0'?'#eee':'#ddd'} strokeWidth="0.6"/>
          <text x={x+c*cw+cw/2-1} y={y+r*ch+ch/2+5}
            textAnchor="middle" fontSize="11"
            fill={val==='0'?'#ccc':color==='RED'?RED:BLUE}
            fontWeight={val!=='0'?'700':'400'}
            fontFamily="'Courier New',monospace">{val}</text>
        </g>
      ))
    );
    const bColor = color==='RED' ? RED : BLUE;
    return (
      <g>
        {/* Brackets */}
        <line x1={x-6} y1={y-3} x2={x-6} y2={y+n*ch+3} stroke={bColor} strokeWidth="2.2"/>
        <line x1={x+m*cw+4} y1={y-3} x2={x+m*cw+4} y2={y+n*ch+3} stroke={bColor} strokeWidth="2.2"/>
        <line x1={x-6} y1={y-3} x2={x} y2={y-3} stroke={bColor} strokeWidth="2.2"/>
        <line x1={x-6} y1={y+n*ch+3} x2={x} y2={y+n*ch+3} stroke={bColor} strokeWidth="2.2"/>
        <line x1={x+m*cw+4} y1={y-3} x2={x+m*cw-2} y2={y-3} stroke={bColor} strokeWidth="2.2"/>
        <line x1={x+m*cw+4} y1={y+n*ch+3} x2={x+m*cw-2} y2={y+n*ch+3} stroke={bColor} strokeWidth="2.2"/>
        {els}
        <text x={x+m*cw/2} y={y-10} textAnchor="middle" fontSize="13" fill={bColor} fontWeight="700">{label}</text>
      </g>
    );
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Canonical Realizations — Three Standard Forms for n=3, G(s) = (b₁s+b₀)/(s³+a₂s²+a₁s+a₀)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* ── Controllable Canonical Form ── */}
        <rect x={8} y={10} width={270} height={310} rx={5} fill="#fff" stroke={BORDER}/>
        <text x={143} y={28} textAnchor="middle" fontSize="12" fill={RED} fontWeight="700">Controllable Canonical</text>

        {renderMatrix(18, 44, [
          ['0','1','0'],
          ['0','0','1'],
          ['-a₀','-a₁','-a₂'],
        ], 'RED', 'A_c')}

        {renderMatrix(188, 44, [
          ['0'],
          ['0'],
          ['1'],
        ], 'BLUE', 'B_c')}

        <text x={18} y={192} fontSize="11" fill={BLUE} fontWeight="700">C_c = [b₀  b₁  0]</text>
        <text x={18} y={210} fontSize="10" fill="#555">Companion matrix form</text>
        <text x={18} y={226} fontSize="10" fill="#555">Char. poly coeffs in last row</text>
        <text x={18} y={246} fontSize="10" fill={GREEN} fontWeight="700">✓ Always controllable</text>
        <text x={18} y={262} fontSize="10" fill="#555">Best for pole placement via K</text>
        <text x={18} y={282} fontSize="10" fill={ORANGE}>Ackermann: K computed directly</text>
        <text x={18} y={298} fontSize="10" fill="#555">from this form</text>

        {/* ── Observable Canonical Form ── */}
        <rect x={292} y={10} width={270} height={310} rx={5} fill="#fff" stroke={BORDER}/>
        <text x={427} y={28} textAnchor="middle" fontSize="12" fill={BLUE} fontWeight="700">Observable Canonical</text>

        {renderMatrix(302, 44, [
          ['0','0','-a₀'],
          ['1','0','-a₁'],
          ['0','1','-a₂'],
        ], 'BLUE', 'A_o')}

        {renderMatrix(472, 44, [
          ['b₀'],
          ['b₁'],
          ['0'],
        ], 'RED', 'B_o')}

        <text x={302} y={192} fontSize="11" fill={BLUE} fontWeight="700">C_o = [0  0  1]</text>
        <text x={302} y={210} fontSize="10" fill="#555">Transpose of controllable form</text>
        <text x={302} y={226} fontSize="10" fill="#555">Char. poly in last column</text>
        <text x={302} y={246} fontSize="10" fill={GREEN} fontWeight="700">✓ Always observable</text>
        <text x={302} y={262} fontSize="10" fill="#555">Best for observer (L) design</text>
        <text x={302} y={282} fontSize="10" fill={ORANGE}>Dual: design L as if designing K</text>
        <text x={302} y={298} fontSize="10" fill="#555">for (Aᵀ, Cᵀ)</text>

        {/* ── Diagonal / Modal Form ── */}
        <rect x={576} y={10} width={316} height={310} rx={5} fill="#fff" stroke={BORDER}/>
        <text x={734} y={28} textAnchor="middle" fontSize="12" fill={PURPLE} fontWeight="700">Diagonal / Modal Form</text>

        {renderMatrix(586, 44, [
          ['λ₁','0','0'],
          ['0','λ₂','0'],
          ['0','0','λ₃'],
        ], 'RED', 'A_d')}

        <text x={748} y={60} fontSize="13" fill={PURPLE} fontWeight="700">A_d = diag(λ₁,λ₂,λ₃)</text>
        <text x={748} y={78} fontSize="10" fill="#555">Eigenvalues on diagonal</text>

        <text x={586} y={192} fontSize="11" fill={PURPLE} fontWeight="700">Modes fully decoupled</text>
        <text x={586} y={210} fontSize="10" fill="#555">ẋᵢ = λᵢxᵢ + bᵢu  (independent ODEs)</text>
        <text x={586} y={230} fontSize="10" fill="#555">Obtained by change of basis: x = Tz</text>
        <text x={586} y={248} fontSize="10" fill="#555">where T = modal matrix (eigenvectors)</text>
        <text x={586} y={268} fontSize="10" fill={GREEN} fontWeight="700">Best for analysis and simulation</text>
        <text x={586} y={284} fontSize="10" fill="#555">Response = sum of modal contributions</text>
        <text x={586} y={300} fontSize="10" fill={ORANGE}>Requires distinct eigenvalues</text>
      </svg>
      <div style={S.dCaption}>
        Any transfer function G(s) can be realised in multiple equivalent state-space forms. The controllable canonical form places characteristic polynomial coefficients in the last row of A — ideal for control design. The observable canonical form (its transpose) is ideal for observer design. The diagonal/modal form decouples the modes completely, making analysis and simulation straightforward. All forms represent the same input-output behaviour.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Diagram 10 — LQR Optimal Control trade-off surface
// ════════════════════════════════════════════════════════════════════
function DiagramLQR() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>LQR — Linear Quadratic Regulator: Optimal State-Feedback Design</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <Defs/>

        {/* Left: Cost function anatomy */}
        <rect x={10} y={10} width={390} height={320} rx={6} fill="#fff" stroke={BORDER}/>
        <text x={205} y={30} textAnchor="middle" fontSize="13" fill={DKRED} fontWeight="700">LQR Cost Function J</text>

        <rect x={20} y={40} width={370} height={60} rx={4} fill={LTRED} stroke={RED} strokeWidth="1.5"/>
        <text x={205} y={65} textAnchor="middle" fontSize="13" fill={RED} fontWeight="700" fontFamily="'Courier New',monospace">J = ∫₀^∞ (xᵀQx + uᵀRu) dt</text>
        <text x={205} y={84} textAnchor="middle" fontSize="10" fill="#555">Minimise this over all u(t) — subject to ẋ=Ax+Bu</text>

        {[
          { y:115, color:BLUE,   bg:LTBLU,  label:'Q  (n×n, pos. semi-def.)', desc:'Penalises state deviation xᵀQx', tip:'Large Q → fast state regulation; poles pulled left' },
          { y:170, color:ORANGE, bg:LTORG,  label:'R  (p×p, pos. def.)',       desc:'Penalises control effort uᵀRu', tip:'Large R → small u; poles closer to open-loop' },
          { y:225, color:GREEN,  bg:LTGRN,  label:'P  (n×n, Riccati solution)', desc:'Solution of Algebraic Riccati Eqn', tip:'AᵀP+PA−PBR⁻¹BᵀP+Q = 0' },
          { y:280, color:PURPLE, bg:LTPUR,  label:'K = R⁻¹BᵀP',               desc:'Optimal gain matrix (1×n for SISO)', tip:'Guaranteed stable: Re(eig(A−BK)) < 0' },
        ].map(({ y, color, bg, label, desc, tip }) => (
          <g key={y}>
            <rect x={20} y={y} width={370} height={50} rx={3} fill={bg} stroke={color} strokeWidth="1"/>
            <text x={28} y={y+16} fontSize="12" fill={color} fontWeight="700" fontFamily="'Courier New',monospace">{label}</text>
            <text x={28} y={y+30} fontSize="10" fill="#444">{desc}</text>
            <text x={28} y={y+44} fontSize="10" fill={color} fontStyle="italic">{tip}</text>
          </g>
        ))}

        {/* Right: Q/R trade-off visualisation */}
        <rect x={415} y={10} width={475} height={320} rx={6} fill="#fff" stroke={BORDER}/>
        <text x={652} y={30} textAnchor="middle" fontSize="13" fill={DKRED} fontWeight="700">Q/R Trade-off — Performance vs Control Effort</text>

        {/* Axes */}
        {(() => {
          const bx = 440, by2 = 50, bw = 220, bh = 220;
          const toX = q => bx + (Math.log10(q)+1)/3 * bw;
          const toY = r => by2 + bh - (Math.log10(r)+1)/3 * bh;
          const qs = [0.1,0.3,1,3,10,30,100];
          const rs = [0.1,0.3,1,3,10,30,100];

          return (
            <g>
              <rect x={bx} y={by2} width={bw} height={bh} rx={3} fill="#fafafa" stroke={BORDER}/>
              {/* Grid */}
              {qs.map(q => (
                <line key={q} x1={toX(q)} y1={by2} x2={toX(q)} y2={by2+bh} stroke="#f0f0f0" strokeWidth="1"/>
              ))}
              {rs.map(r => (
                <line key={r} x1={bx} y1={toY(r)} x2={bx+bw} y2={toY(r)} stroke="#f0f0f0" strokeWidth="1"/>
              ))}

              {/* Contours: iso-performance curves (approximate ellipses) */}
              {[0.6,0.8,1.0,1.2].map((scale, i) => {
                const colors = [RED, ORANGE, GREEN, BLUE];
                const cx2 = bx + bw*0.5, cy2 = by2 + bh*0.5;
                const rx2 = bw*0.18*scale, ry2 = bh*0.14*scale;
                return (
                  <ellipse key={i} cx={cx2} cy={cy2} rx={rx2} ry={ry2}
                    fill="none" stroke={colors[i]} strokeWidth="1.5" opacity="0.6" strokeDasharray="4 2"/>
                );
              })}

              {/* Axes */}
              <line x1={bx} y1={by2+bh} x2={bx+bw} y2={by2+bh} stroke="#bbb" strokeWidth="1.3"/>
              <line x1={bx} y1={by2} x2={bx} y2={by2+bh} stroke="#bbb" strokeWidth="1.3"/>
              <text x={bx+bw/2} y={by2+bh+18} textAnchor="middle" fontSize="11" fill="#777">← Q (state weight) →</text>
              <text x={bx-28} y={by2+bh/2} textAnchor="middle" fontSize="11" fill="#777" transform={`rotate(-90,${bx-28},${by2+bh/2})`}>← R (control weight) →</text>

              {/* Annotated points */}
              {[
                { q:1,   r:10,  label:'Conservative',  desc:'Small u; slow poles', color:BLUE },
                { q:10,  r:1,   label:'Aggressive',    desc:'Large u; fast poles', color:RED },
                { q:1,   r:1,   label:'Balanced',      desc:'Typical starting pt', color:GREEN },
              ].map(({ q, r, label, desc, color }) => {
                const px = toX(q), py2p = toY(r);
                return (
                  <g key={label}>
                    <circle cx={px} cy={py2p} r={6} fill={color} opacity="0.85"/>
                    <text x={px+8} y={py2p-4} fontSize="10" fill={color} fontWeight="700">{label}</text>
                    <text x={px+8} y={py2p+8} fontSize="9" fill="#666">{desc}</text>
                  </g>
                );
              })}
            </g>
          );
        })()}

        {/* Right side bullets */}
        <rect x={680} y={50} width={200} height={280} rx={4} fill="#fff" stroke={BORDER}/>
        <text x={780} y={68} textAnchor="middle" fontSize="12" fill={DKRED} fontWeight="700">LQR Properties</text>
        {[
          { y:76,  bg:LTGRN, bc:GREEN,  lines:['Always stable','Closed-loop guaranteed stable','for any Q≥0, R>0 (if controllable)'] },
          { y:130, bg:LTBLU, bc:BLUE,   lines:['Guaranteed margins','GM ≥ 6 dB (≥2× gain)','PM ≥ 60° (classical result)'] },
          { y:184, bg:LTORG, bc:ORANGE, lines:['Systematic tuning','Adjust Q,R weights to','meet time-domain specs'] },
          { y:238, bg:LTPUR, bc:PURPLE, lines:['Riccati equation','Solved by MATLAB: lqr(A,B,Q,R)','Returns optimal P and K'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={686} y={y} width={188} height={50} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={692} y={y+15+i*15} fontSize={i===0?'11':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        LQR finds the gain matrix K that minimises the quadratic cost J = ∫(xᵀQx + uᵀRu)dt. The Q matrix weights how much we penalise state errors; R weights how much we penalise control effort. A larger Q/R ratio drives the poles further left (faster, more aggressive control). LQR guarantees closed-loop stability and provides classical robustness margins (GM ≥ 6 dB, PM ≥ 60°) — a powerful advantage over ad-hoc pole placement.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Main
// ════════════════════════════════════════════════════════════════════
export default function StateSpace() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>State-Space Modeling in Control Systems</h1>
      <p style={S.p}>
        Classical control methods — transfer functions, Bode plots, root locus — describe a system through its input-output behaviour alone. State-space modeling goes deeper: it represents the <em>internal dynamics</em> of a system as a set of first-order differential equations in terms of state variables, capturing quantities like velocity, charge, temperature, or angle that evolve over time. Developed in the late 1950s alongside the space race, state-space methods are essential for multi-input multi-output (MIMO) systems, optimal control, and modern controller design.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "State equation: ẋ = Ax + Bu  (n first-order ODEs — one per state variable)",
        "Output equation: y = Cx + Du  (algebraic mapping from states and inputs to outputs)",
        "Stability ↔ eigenvalues of A: all must have negative real parts (left-half plane)",
        "State-space ↔ transfer function: G(s) = C(sI−A)⁻¹B + D  (unique; inverse gives many realizations)",
        "Controllability: can all states be driven to any target? — rank(𝒞) = n",
        "Observability: can all states be reconstructed from output? — rank(𝒪) = n",
        "Full-state feedback: u = −Kx places closed-loop poles anywhere (if controllable)",
        "Luenberger observer: estimates states from outputs; poles of (A−LC) must be faster than (A−BK)",
        "LQR: optimal K from minimising J = ∫(xᵀQx + uᵀRu)dt; solves the Algebraic Riccati Equation",
      ]}/>

      <h2 style={S.h2}>The State-Space Block Diagram</h2>
      <p style={S.p}>
        The state-space model can be drawn as a block diagram showing exactly how signals flow through the four matrices. Input u(t) is scaled by B, added to the state feedback Ax, and integrated to produce the state x(t). The output y(t) is formed by C·x plus direct feedthrough D·u. Every linear time-invariant system — regardless of its physical origin — can be written in this form.
      </p>
      <DiagramBlockDiagram/>
      <Table2 rows={[
        ["State vector x(t)","n×1 vector of internal variables fully characterising system memory\nExamples: [position, velocity], [inductor current, capacitor voltage]"],
        ["State equation ẋ=Ax+Bu","n first-order ODEs; equivalent to one nth-order ODE\nA: dynamics; B: how input drives states"],
        ["Output equation y=Cx+Du","q algebraic equations; no derivatives of y appear\nC: which states are observed; D: direct feedthrough (usually 0)"],
        ["Order n","Number of state variables = number of integrators\nEquals the degree of the denominator polynomial of G(s)"],
        ["Time-invariant (LTI)","A, B, C, D are constant matrices (no time-varying entries)\nFor LTV systems, matrices become A(t), B(t), etc."],
        ["Discrete-time form","x[k+1] = Ax[k] + Bu[k];  y[k] = Cx[k] + Du[k]\nSame structure; difference equation instead of differential"],
      ]}/>

      <h2 style={S.h2}>Matrix Anatomy — What Every Entry Means</h2>
      <p style={S.p}>
        The four matrices A, B, C, D each have precise physical interpretations. Understanding what each entry represents — not just how to manipulate them algebraically — is essential for building intuition about system behaviour and for designing effective controllers.
      </p>
      <DiagramMatrixAnatomy/>
      <Table2 rows={[
        ["A matrix — aᵢⱼ entry","Rate at which state xⱼ influences ẋᵢ\nDiagonal aᵢᵢ: self-coupling (negative = self-stabilising mode); off-diagonal: inter-state coupling"],
        ["B matrix — bᵢⱼ entry","How input uⱼ drives ẋᵢ\nIf bᵢⱼ = 0: input j has no direct effect on state i's derivative"],
        ["C matrix — cᵢⱼ entry","Weight of state xⱼ in output yᵢ\nRow i of C defines which linear combination of states forms the i-th output"],
        ["D matrix — dᵢⱼ entry","Direct gain from input uⱼ to output yᵢ (bypassing dynamics)\nFor most physical systems D=0; non-zero in electrical circuits with capacitor-less paths"],
        ["Characteristic polynomial","det(sI−A) = sⁿ + aₙ₋₁sⁿ⁻¹ + ··· + a₀\nCoefficients determine all natural frequencies and damping — same as TF denominator"],
        ["State transformation","If x = Tz, new matrices: Ã=T⁻¹AT, B̃=T⁻¹B, C̃=CT, D̃=D\nEigenvalues invariant under similarity transform; useful for canonical forms"],
      ]}/>

      <h2 style={S.h2}>State-Space vs Transfer Function</h2>
      <p style={S.p}>
        State-space and transfer function descriptions are complementary, not competing. The transfer function is the external (input-output) description; state-space is the internal description. Each has strengths: transfer functions are natural for frequency-domain design; state-space is essential for MIMO systems, time-domain optimal control, and cases where the internal states carry physical meaning.
      </p>
      <DiagramSSvsTF/>
      <Table2 rows={[
        ["SS → TF (unique)","G(s) = C(sI−A)⁻¹B + D\nApply Laplace, solve for X(s), substitute into Y(s) = CX(s) + DU(s)"],
        ["TF → SS (non-unique)","Infinitely many SS realizations — all equivalent input-output behaviour\nMost common: controllable canonical, observable canonical, modal (diagonal)"],
        ["Minimal realization","Smallest n with same G(s); achieved when system is both controllable AND observable\nNon-minimal: some pole-zero cancellation exists — hidden uncontrollable or unobservable modes"],
        ["MIMO transfer matrix","G(s) is a q×p matrix of transfer functions: Gᵢⱼ(s) = yᵢ(s)/uⱼ(s)|all other u=0\nState-space handles this naturally; scalar TF methods do not"],
        ["Poles in both forms","TF poles = roots of det(sI−A) = eigenvalues of A\nExact correspondence for minimal realizations"],
        ["Zeros","TF zeros ≠ eigenvalues of A in general\nSS zeros: values of s where rank of [sI−A  −B; C  D] drops below normal"],
      ]}/>

      <h2 style={S.h2}>Eigenvalues of A — Stability and System Modes</h2>
      <p style={S.p}>
        The eigenvalues of the A matrix are the most important quantity in state-space analysis. Each eigenvalue corresponds to a natural mode of the system — a specific pattern of motion that the system can exhibit. The real part of each eigenvalue determines exponential growth or decay; the imaginary part determines oscillation frequency. A system is stable if and only if all eigenvalues have strictly negative real parts.
      </p>
      <DiagramEigenvalues/>
      <Table2 rows={[
        ["Eigenvalue equation","Aυ = λυ  →  det(λI−A) = 0\nλ: eigenvalue, υ: eigenvector (mode shape)"],
        ["Characteristic polynomial","det(sI−A) = 0 gives the same polynomial as the TF denominator\nFor a minimal realization, TF poles = eigenvalues of A exactly"],
        ["Real negative λ = −a","Mode: e^{−at} — pure exponential decay\nFastest decay when |λ| large; time constant τ = 1/|λ|"],
        ["Complex λ = −σ±jω","Mode: e^{−σt}sin(ωt+φ) — damped sinusoid\nDamping ratio ζ=σ/|λ|; natural freq ωₙ=|λ|=√(σ²+ω²)"],
        ["Repeated eigenvalues","Jordan block: response includes t·e^{λt} terms — polynomial × exponential\nOnly for non-generic matrices; avoided in practice through proper design"],
        ["Spectral decomposition","A = VΛV⁻¹  (Λ=diag eigenvalues, V=eigenvector matrix)\nAllows modal decomposition: x(t) = Σcᵢ vᵢ e^{λᵢt}"],
        ["Matrix exponential","e^{At} = Ve^{Λt}V⁻¹ = Σ vᵢ e^{λᵢt} wᵢᵀ\nFundamental matrix for free response; computed as sum of mode contributions"],
      ]}/>

      <h2 style={S.h2}>Phase Portraits — Visualising State Trajectories</h2>
      <p style={S.p}>
        For 2D systems, the phase portrait — a plot of x₂ vs x₁ — gives an immediate visual picture of system behaviour. Every point in the state space has a unique velocity vector (ẋ₁, ẋ₂); the collection of all such vectors forms a vector field, and trajectories flow along these vectors. The type of equilibrium point (node, spiral, saddle, centre) is determined entirely by the eigenvalues of A.
      </p>
      <DiagramPhasePortrait/>
      <Table2 rows={[
        ["Stable node","Two real negative eigenvalues: λ₁ < λ₂ < 0\nTrajectories converge straight to origin along eigenvector directions"],
        ["Unstable node","Two real positive eigenvalues: λ₁ > λ₂ > 0\nTrajectories diverge from origin along eigenvector directions"],
        ["Saddle point","One positive, one negative real eigenvalue\nStable manifold attracts; unstable manifold repels — inherently unstable"],
        ["Stable spiral","Complex eigenvalues with σ < 0\nTrajectories corkscrew inward; rotation direction determined by sign of Im(λ)"],
        ["Unstable spiral","Complex eigenvalues with σ > 0\nTrajectories spiral outward without bound"],
        ["Centre (marginal)","Pure imaginary eigenvalues: λ = ±jω\nClosed elliptical orbits — conservative system (e.g. undamped mass-spring)"],
        ["Nonlinear systems","Linearise around equilibrium x*: A = ∂f/∂x|_{x*}\nLocal stability determined by eigenvalues of Jacobian matrix A"],
      ]}/>

      <h2 style={S.h2}>Controllability and Observability</h2>
      <p style={S.p}>
        Two structural properties determine what is possible with state-space control: controllability (can we steer the states?) and observability (can we see the states?). Both can be checked by simple rank tests on matrices formed from A, B, C. They are dual to each other — the controllability of (A, B) mirrors the observability of (Aᵀ, Cᵀ).
      </p>
      <DiagramControllabilityObservability/>
      <Table2 rows={[
        ["Controllability matrix 𝒞","𝒞 = [B  AB  A²B  ···  Aⁿ⁻¹B]  (n×np matrix)\nrank(𝒞) = n: fully controllable; rank < n: some modes unreachable"],
        ["Observability matrix 𝒪","𝒪 = [C; CA; CA²; ···; CAⁿ⁻¹]ᵀ  (nq×n matrix)\nrank(𝒪) = n: fully observable; rank < n: some modes hidden in output"],
        ["PBH test (eigenvalue test)","System uncontrollable iff ∃ λ: rank[A−λI  B] < n\nSystem unobservable iff ∃ λ: rank[A−λI; C] < n"],
        ["Pole-zero cancellation","Cancellation in G(s) ↔ uncontrollable OR unobservable mode\nCancelled poles disappear from TF but still exist internally — danger!"],
        ["Gramians","Controllability Gramian Wc = ∫e^{At}BBᵀe^{Aᵀt}dt  (solves AWc+WcAᵀ+BBᵀ=0)\nObservability Gramian Wo solves AᵀWo+WoA+CᵀC=0; used for model reduction"],
        ["Stabilisability & Detectability","Weaker conditions: unstable modes must be controllable / observable\nSufficient for practical controller design even if stable modes are not reachable"],
      ]}/>

      <h2 style={S.h2}>Full-State Feedback and Pole Placement</h2>
      <p style={S.p}>
        If all states are measurable, the control law u = −Kx + r places the closed-loop poles (eigenvalues of A−BK) at any desired locations in the s-plane — provided the system is controllable. This is the state-space counterpart of root locus design, but far more systematic: any set of desired poles can be achieved in one step.
      </p>
      <DiagramPolePlacement/>
      <Table2 rows={[
        ["Control law","u(t) = −Kx(t) + Nr(t)   (r = reference, N = pre-filter for steady-state)\nK is a 1×n gain vector (SISO) or p×n matrix (MIMO)"],
        ["Closed-loop system","ẋ = (A−BK)x + BNr;  y = Cx\nAll closed-loop poles = eigenvalues of A−BK"],
        ["Ackermann's formula","K = eₙᵀ 𝒞⁻¹ φ(A)\nφ(A) = desired characteristic polynomial evaluated at A (Cayley-Hamilton)"],
        ["Bass-Gura method","Match coefficients of det(sI−(A−BK)) to desired char. poly\nRequires system in controllable canonical form"],
        ["MATLAB: place(A,B,p)","Numerical pole placement; p = desired pole vector\nMore numerically robust than Ackermann for high-order systems"],
        ["Pre-filter N","N = −1/[C(A−BK)⁻¹B] for zero steady-state error (step input)\nEnsures y_ss = r_ss; otherwise closed-loop DC gain ≠ 1"],
        ["Integral action","Augment state with xᵢ = ∫(r−y)dt; expand A,B,C matrices\nGuarantees zero steady-state error without pre-filter — robust to parameter uncertainty"],
      ]}/>

      <h2 style={S.h2}>Luenberger Observer and Observer-Based Control</h2>
      <p style={S.p}>
        In practice, not all states are directly measurable — we may only have output sensors. The Luenberger observer solves this by running a software copy of the plant, continually correcting it using the discrepancy between the real output y and the model's predicted output Cx̂. The correction term L(y−Cx̂) drives the estimation error to zero exponentially.
      </p>
      <DiagramObserver/>
      <Table2 rows={[
        ["Observer equation","x̂̇ = Ax̂ + Bu + L(y − Cx̂) = (A−LC)x̂ + Bu + Ly\nL is the n×q observer gain matrix"],
        ["Error dynamics","Define e = x − x̂: ė = (A−LC)e\nError decays as e^{(A−LC)t} — same pole-placement problem as state feedback"],
        ["Observer pole placement","Choose L so eigenvalues of (A−LC) are 2–5× faster than (A−BK) poles\nFaster observer = quicker state estimate convergence but more noise amplification"],
        ["Separation principle","With observer: u=−Kx̂, closed-loop poles = eig(A−BK) ∪ eig(A−LC)\nK and L can be designed independently — remarkable and powerful result"],
        ["Duality","Observer design for (A,C) = pole placement for (Aᵀ,Cᵀ)\nL = Kᵒᵇˢᵀ where Kᵒᵇˢ is found by placing poles of (Aᵀ−CᵀLᵀ)"],
        ["Kalman filter","Optimal observer when plant disturbance and measurement noise are Gaussian\nL_opt = PCᵀR⁻¹ (P from Riccati); minimises estimation error variance"],
        ["Output feedback","Combined observer + state feedback = dynamic output feedback controller\nTransfer function from y to u: K(sI−A+BK+LC)⁻¹L"],
      ]}/>

      <h2 style={S.h2}>Canonical Realizations</h2>
      <p style={S.p}>
        When converting a transfer function to state-space, the choice of state variables is not unique — any invertible change of basis gives an equivalent realization. Three canonical forms are particularly useful: controllable canonical (ideal for pole placement), observable canonical (ideal for observer design), and diagonal/modal (ideal for analysis).
      </p>
      <DiagramCanonicalForms/>
      <Table2 rows={[
        ["Controllable canonical","A has companion matrix structure; characteristic poly coefficients in last row\nB = [0;0;…;1]; directly amenable to Ackermann's formula"],
        ["Observable canonical","Transpose of controllable canonical form; char. poly in last column\nC = [0 0 … 1]; directly amenable to Ackermann's formula for L"],
        ["Diagonal (modal) form","A = diag(λ₁,…,λₙ); B = V⁻¹B_orig; C = C_orig·V  (V = eigenvector matrix)\nModes fully decoupled; easy to analyse individual mode contributions"],
        ["Jordan form","Generalisation of diagonal for repeated eigenvalues\nJordan blocks for each repeated eigenvalue; response includes polynomial × exponential terms"],
        ["Change of basis","x = Tz transforms: Ã=T⁻¹AT, B̃=T⁻¹B, C̃=CT, D̃=D\nEigenvalues and TF invariant; only state labels change"],
        ["Balanced realization","Controllability Gramian = Observability Gramian = Σ (diagonal)\nDiagonal entries = Hankel singular values; basis for model reduction (truncate small ones)"],
      ]}/>

      <h2 style={S.h2}>LQR — Linear Quadratic Regulator</h2>
      <p style={S.p}>
        Rather than choosing closed-loop pole locations directly, LQR finds the optimal gain matrix K by minimising a quadratic cost function that trades off state deviation against control effort. The solution comes from the Algebraic Riccati Equation and is guaranteed to be stable with classical robustness margins — making it one of the most widely used practical design methods.
      </p>
      <DiagramLQR/>
      <Table2 rows={[
        ["Cost function","J = ∫₀^∞ (xᵀQx + uᵀRu) dt  →  minimise over u(t) subject to ẋ=Ax+Bu\nQ penalises state error; R penalises control effort"],
        ["Algebraic Riccati Equation","AᵀP + PA − PBR⁻¹BᵀP + Q = 0\nSolve for positive definite P (always exists if (A,B) stabilisable, (A,Q^{1/2}) detectable)"],
        ["Optimal gain","K = R⁻¹BᵀP\nPlug into u = −Kx; closed-loop A−BK is guaranteed Hurwitz (stable)"],
        ["Robustness guarantees","GM ≥ 6 dB (gain can double or halve), PM ≥ 60° at each input channel\nHolds for full-state feedback LQR — remarkable classical result"],
        ["Choosing Q and R","Q = CᵀC or diagonal with weights on important states; R = identity or scaled\nBryson's rule: Qᵢᵢ = 1/(max acceptable xᵢ)², Rⱼⱼ = 1/(max acceptable uⱼ)²"],
        ["LQG (with observer)","LQR + Kalman filter = Linear Quadratic Gaussian controller\nOptimal output feedback; K from LQR, L from Kalman filter (dual Riccati)"],
        ["MATLAB","[K,P,e] = lqr(A,B,Q,R)  — returns gain K, Riccati solution P, closed-loop eigenvalues e\nK = lqr(sys,Q,R) also works with state-space system objects"],
      ]}/>

      <h2 style={S.h2}>Summary: State-Space Design Workflow</h2>
      <Table2 rows={[
        ["Step 1 — Model","Write equations of motion / KVL-KCL / energy methods\nDefine state variables; extract A, B, C, D matrices"],
        ["Step 2 — Verify","Check eigenvalues of A for open-loop stability\nSimulate step response; compare to expected behaviour"],
        ["Step 3 — Check structure","Compute rank(𝒞) = n? (controllable) and rank(𝒪) = n? (observable)\nIf not, identify which modes are uncontrollable/unobservable and why"],
        ["Step 4 — Design K","Specify desired closed-loop poles (from %OS, ts, tr specs)\nUse Ackermann / place / LQR to find K"],
        ["Step 5 — Design L (if needed)","Specify observer poles (2–5× faster than closed-loop)\nUse duality / place on (Aᵀ,Cᵀ) to find L; or use Kalman filter for optimal L"],
        ["Step 6 — Simulate","Simulate observer + controller together\nVerify state estimates converge before control engages fully"],
        ["Step 7 — Validate robustness","Check GM and PM of the combined system\nTest with parameter variations; consider LQG/LTR for loop recovery if needed"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "State equation: ẋ(t) = A·x(t) + B·u(t)   |   Output: y(t) = C·x(t) + D·u(t)",
        "Transfer function: G(s) = C(sI−A)⁻¹B + D   (unique from state-space matrices)",
        "Solution: x(t) = e^{At}x(0) + ∫₀ᵗ e^{A(t−τ)}Bu(τ)dτ   (matrix exponential)",
        "Stability: all eigenvalues of A have Re(λ) < 0  ⟺  all TF poles in LHP",
        "Controllability: rank[B  AB  ···  Aⁿ⁻¹B] = n",
        "Observability: rank[Cᵀ  AᵀCᵀ  ···  (Aᵀ)ⁿ⁻¹Cᵀ] = n",
        "State feedback closed-loop: ẋ = (A−BK)x   |   place eigenvalues of A−BK",
        "Observer error: ė = (A−LC)e → 0   |   place eigenvalues of A−LC (faster than A−BK)",
        "LQR: K = R⁻¹BᵀP  where  AᵀP+PA−PBR⁻¹BᵀP+Q = 0  (Algebraic Riccati Equation)",
      ]}/>
    </div>
  );
}